<template>
  <div>
    <div class="flex justify-between items-center q-mb-lg">
      <h3 class="admin-page-title">Externa event</h3>
      <div class="text-caption text-grey-7">
        {{ events.length }} träffar
      </div>
    </div>

    <div class="filters q-mb-md">
      <q-input
        v-model="search"
        label="Sök på titel eller venue"
        outlined
        dense
        clearable
        debounce="300"
        @update:model-value="fetchEvents"
        class="filter-search"
      />
      <q-toggle
        v-model="includePast"
        label="Visa även passerade event"
        @update:model-value="fetchEvents"
      />
    </div>

    <q-table
      :rows="events"
      :columns="columns"
      row-key="_id"
      :loading="loading"
      :pagination="{ rowsPerPage: 50 }"
    >
      <template v-slot:body-cell-imageUrl="props">
        <q-td :props="props">
          <q-img
            v-if="props.row.imageUrl"
            :src="props.row.imageUrl"
            alt=""
            style="width: 56px; height: 56px"
            class="thumb"
          />
        </q-td>
      </template>

      <template v-slot:body-cell-date="props">
        <q-td :props="props">
          {{ formatDate(props.row.date) }}
        </q-td>
      </template>

      <template v-slot:body-cell-genre="props">
        <q-td :props="props">
          <q-badge v-if="props.row.genre" color="dark">
            {{ props.row.genre }}
          </q-badge>
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-highlighted="props">
        <q-td :props="props" class="text-center">
          <q-btn
            flat
            round
            dense
            :icon="props.row.highlighted ? 'star' : 'star_border'"
            :color="props.row.highlighted ? 'amber' : 'grey-5'"
            @click="toggleHighlight(props.row)"
            :aria-label="
              props.row.highlighted
                ? 'Ta bort highlight'
                : 'Highlighta eventet'
            "
          />
        </q-td>
      </template>

      <template v-slot:body-cell-delete="props">
        <q-td :props="props" class="text-center">
          <q-btn
            flat
            round
            dense
            icon="delete"
            color="negative"
            @click="confirmDelete(props.row)"
            aria-label="Radera event"
          />
        </q-td>
      </template>
    </q-table>
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import { useQuasar } from "quasar";
import axios from "axios";
import { useRouter } from "vue-router";
import { clearAuth } from "@/utils/auth";

export default {
  name: "AdminExternalEvents",
  setup() {
    const $q = useQuasar();
    const router = useRouter();

    const events = ref([]);
    const loading = ref(false);
    const search = ref("");
    const includePast = ref(false);

    const columns = [
      { name: "imageUrl", label: "", align: "center" },
      { name: "title", label: "Titel", field: "title", align: "left", sortable: true },
      { name: "venue", label: "Venue", field: "venue", align: "left", sortable: true },
      { name: "date", label: "Datum", field: "date", align: "left", sortable: true },
      { name: "genre", label: "Genre", align: "center" },
      { name: "highlighted", label: "Highlight", align: "center" },
      { name: "delete", label: "Radera", align: "center" },
    ];

    const formatDate = (d) => {
      if (!d) return "";
      try {
        return new Date(d).toLocaleDateString("sv-SE", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (e) {
        return d;
      }
    };

    const authHeader = () => {
      const token = localStorage.getItem("token");
      return { Authorization: `Bearer ${token}` };
    };

    const handleAuthError = (err) => {
      const status = err?.response?.status;
      if (status === 401) {
        $q.notify({ type: "negative", message: "Logga in igen." });
        clearAuth();
        router.push("/login");
        return true;
      }
      if (status === 403) {
        $q.notify({
          type: "negative",
          message: "Du har inte behörighet för den här vyn.",
        });
        router.push("/admin");
        return true;
      }
      return false;
    };

    const fetchEvents = async () => {
      loading.value = true;
      try {
        const params = {
          search: search.value || undefined,
          includePast: includePast.value || undefined,
          limit: 200,
        };
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "admin/external-events",
          { headers: authHeader(), params }
        );
        events.value = data;
      } catch (err) {
        if (!handleAuthError(err)) {
          $q.notify({
            type: "negative",
            message: "Kunde inte hämta externa event",
          });
        }
      } finally {
        loading.value = false;
      }
    };

    const toggleHighlight = async (row) => {
      // Optimistisk uppdatering så stjärnan flippar direkt
      const previous = !!row.highlighted;
      row.highlighted = !previous;
      try {
        const { data } = await axios.patch(
          process.env.VUE_APP_API_URL +
            "admin/external-events/" +
            row._id,
          { highlighted: row.highlighted },
          { headers: authHeader() }
        );
        // Uppdatera raden med serverns svar (säkrar att vi har rätt state)
        const idx = events.value.findIndex((e) => e._id === data._id);
        if (idx !== -1) events.value[idx] = data;
      } catch (err) {
        // Återställ vid fel
        row.highlighted = previous;
        if (!handleAuthError(err)) {
          $q.notify({
            type: "negative",
            message:
              err.response?.data?.message || "Kunde inte uppdatera highlight",
          });
        }
      }
    };

    const confirmDelete = (row) => {
      $q.dialog({
        title: "Radera event?",
        message: `Vill du verkligen radera "${row.title}"? Detta kan inte ångras.`,
        ok: { label: "Radera", color: "negative" },
        cancel: { label: "Avbryt", flat: true, color: "grey-7" },
      }).onOk(async () => {
        try {
          await axios.delete(
            process.env.VUE_APP_API_URL +
              "admin/external-events/" +
              row._id,
            { headers: authHeader() }
          );
          events.value = events.value.filter((e) => e._id !== row._id);
          $q.notify({ type: "positive", message: "Eventet har raderats!" });
        } catch (err) {
          if (!handleAuthError(err)) {
            $q.notify({
              type: "negative",
              message:
                err.response?.data?.message ||
                "Kunde inte radera eventet",
            });
          }
        }
      });
    };

    onMounted(() => {
      fetchEvents();
    });

    return {
      events,
      loading,
      search,
      includePast,
      columns,
      formatDate,
      fetchEvents,
      toggleHighlight,
      confirmDelete,
    };
  },
};
</script>

<style scoped>
.admin-page-title {
  font-family: "Playfair Display", serif;
  font-size: 1.5rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--color-text);
  margin: 0;
}
.filters {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}
.filter-search { min-width: 280px; flex: 1; }
.thumb {
  border-radius: 4px;
  overflow: hidden;
}
</style>
